/**
 * Éditeur d'un pack de la progression type (admin, lot F, migration 0289) :
 * infos, chapitres, SKILLCARDS (ordre), cartes « à créer » avec lien
 * « Générer la carte » prérempli.
 */

import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowDown, ArrowUp, Plus, Sparkles, X } from 'lucide-react';
import { Button, MathText, PackIconGlyph } from '@jeraisonne/ui';
import {
  PACK_ICONS,
  SCHOOL_MONTHS,
  cardPackCounts,
  monthLongLabel,
  packPeriodIssue,
  packSlug,
} from '@jeraisonne/db/curriculum';
import { normalizeCardSearch, stripMath } from '@jeraisonne/db/cards';
import { useCards } from '@/lib/cards/api';
import {
  useLevelChapters,
  useSavePack,
  type AdminPack,
  type PackCardRef,
  type PackStatus,
} from '@/lib/curriculum/api';

const inputClass =
  'border-ink-200 text-ink-900 focus:border-primary-400 min-h-[2.75rem] w-full rounded-lg border bg-white px-3 py-2 text-sm outline-none';

export function PackEditor({
  level,
  subject,
  pack,
  programmeId,
  allPacks,
  defaultColor,
  onDone,
}: {
  level: string;
  subject: string;
  pack: AdminPack | null;
  /** Tous les packs du niveau / de la matière : une carte peut être dans plusieurs packs. */
  allPacks: readonly AdminPack[];
  defaultColor: string;
  /** Programme officiel auquel le pack appartient (la validité est sur le programme). */
  programmeId: string;
  onDone: () => void;
}) {
  const chapters = useLevelChapters(level, subject);
  const allCards = useCards();
  const save = useSavePack(level, subject);
  const [title, setTitle] = useState(pack?.title ?? '');
  const [hook, setHook] = useState(pack?.hook ?? '');
  const [month, setMonth] = useState(pack?.start_month ?? 9);
  const [position, setPosition] = useState(pack?.position ?? 0);
  const [icon, setIcon] = useState(pack?.icon ?? 'sparkles');
  const [color, setColor] = useState(pack?.color ?? defaultColor);
  const [status, setStatus] = useState<PackStatus>(pack?.status ?? 'draft');
  const [endMonth, setEndMonth] = useState<number>(pack?.end_month ?? pack?.start_month ?? 9);
  const otherPacks = useMemo(() => cardPackCounts(allPacks, pack?.id), [allPacks, pack?.id]);
  const [chapterIds, setChapterIds] = useState<string[]>(pack?.chapter_ids ?? []);
  const [cards, setCards] = useState<PackCardRef[]>(pack?.cards ?? []);
  const [missing, setMissing] = useState(pack?.missing_cards ?? []);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  const candidates = useMemo(() => {
    const q = normalizeCardSearch(query);
    const taken = new Set(cards.map((c) => c.id));
    return (allCards.data ?? [])
      .filter(
        (c) =>
          c.published_version != null &&
          c.status !== 'archived' &&
          c.levels.includes(level) &&
          !taken.has(c.id) &&
          (!q ||
            normalizeCardSearch(`${c.title} ${c.domain_label ?? ''} ${c.short_code}`).includes(q)),
      )
      .sort(
        (a, b) =>
          Number(b.subject === subject) - Number(a.subject === subject) ||
          a.title.localeCompare(b.title, 'fr'),
      )
      .slice(0, 12);
  }, [allCards.data, cards, query, level, subject]);

  const moveCard = (i: number, d: -1 | 1) =>
    setCards((prev) => {
      const next = [...prev];
      const j = i + d;
      if (j < 0 || j >= next.length) return prev;
      [next[i], next[j]] = [next[j]!, next[i]!];
      return next;
    });

  const onSave = async () => {
    setError(null);
    if (title.trim().length < 2) {
      setError('Donne un titre au pack.');
      return;
    }
    const periodIssue = packPeriodIssue(month, endMonth);
    if (periodIssue) {
      setError(periodIssue);
      return;
    }
    try {
      await save.mutateAsync({
        id: pack?.id,
        input: {
          slug:
            pack?.slug ?? `${packSlug(level, subject, title)}-${Date.now().toString(36).slice(-4)}`,
          level,
          subject,
          title: title.trim(),
          hook: hook.trim(),
          start_month: month,
          position,
          color,
          icon,
          status,
          programme_id: pack?.programme_id ?? programmeId,
          end_month: endMonth === month ? null : endMonth,
          missing_cards: missing.filter((m) => m.title.trim().length >= 2),
        },
        chapterIds,
        cardIds: cards.map((c) => c.id),
      });
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div
      className="border-primary-200 space-y-4 rounded-2xl border bg-white p-4"
      data-testid="pack-editor"
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block space-y-1 sm:col-span-2">
          <span className="text-ink-700 text-sm font-semibold">Titre</span>
          <input
            className={inputClass}
            value={title}
            maxLength={80}
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>
        <label className="block space-y-1 sm:col-span-2">
          <span className="text-ink-700 text-sm font-semibold">Accroche</span>
          <input
            className={inputClass}
            value={hook}
            maxLength={240}
            onChange={(e) => setHook(e.target.value)}
          />
        </label>
        <label className="block space-y-1">
          <span className="text-ink-700 text-sm font-semibold">Période indicative : début</span>
          <select
            className={inputClass}
            value={month}
            onChange={(e) => {
              const m = Number(e.target.value);
              setMonth(m);
              if (packPeriodIssue(m, endMonth)) setEndMonth(m);
            }}
          >
            {SCHOOL_MONTHS.map((m) => (
              <option key={m} value={m}>
                {monthLongLabel(m)}
              </option>
            ))}
          </select>
        </label>
        <label className="block space-y-1">
          <span className="text-ink-700 text-sm font-semibold">Période indicative : fin</span>
          <select
            className={inputClass}
            value={endMonth}
            onChange={(e) => setEndMonth(Number(e.target.value))}
          >
            {SCHOOL_MONTHS.map((m) => (
              <option key={m} value={m}>
                {monthLongLabel(m)}
              </option>
            ))}
          </select>
        </label>
        <label className="block space-y-1">
          <span className="text-ink-700 text-sm font-semibold">Ordre dans le mois</span>
          <input
            type="number"
            min={0}
            max={20}
            className={inputClass}
            value={position}
            onChange={(e) => setPosition(Math.max(0, Number(e.target.value) || 0))}
          />
        </label>
        <label className="block space-y-1">
          <span className="text-ink-700 flex items-center gap-2 text-sm font-semibold">
            Icône <PackIconGlyph icon={icon} className="h-4 w-4" />
          </span>
          <select className={inputClass} value={icon} onChange={(e) => setIcon(e.target.value)}>
            {PACK_ICONS.map((i) => (
              <option key={i} value={i}>
                {i}
              </option>
            ))}
          </select>
        </label>
        <label className="block space-y-1">
          <span className="text-ink-700 text-sm font-semibold">Couleur</span>
          <input
            type="color"
            className="h-11 w-full rounded-lg"
            value={color}
            onChange={(e) => setColor(e.target.value)}
          />
        </label>
        <label className="block space-y-1">
          <span className="text-ink-700 text-sm font-semibold">Statut</span>
          <select
            className={inputClass}
            value={status}
            onChange={(e) => setStatus(e.target.value as PackStatus)}
          >
            <option value="draft">Brouillon</option>
            <option value="published">Publié</option>
            <option value="archived">Archivé</option>
          </select>
        </label>
      </div>

      <fieldset className="space-y-1">
        <legend className="text-ink-700 text-sm font-semibold">Chapitres</legend>
        {(chapters.data ?? []).length === 0 && (
          <p className="text-ink-500 text-xs">Aucun chapitre pour ce niveau.</p>
        )}
        <div className="grid gap-1 sm:grid-cols-2">
          {(chapters.data ?? []).map((ch) => (
            <label key={ch.id} className="flex min-h-[2.75rem] items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={chapterIds.includes(ch.id)}
                onChange={(e) =>
                  setChapterIds((prev) =>
                    e.target.checked ? [...prev, ch.id] : prev.filter((x) => x !== ch.id),
                  )
                }
              />
              {ch.title}
            </label>
          ))}
        </div>
      </fieldset>

      <div className="space-y-2">
        <p className="text-ink-700 text-sm font-semibold">SKILLCARDS ({cards.length})</p>
        <ol className="space-y-1">
          {cards.map((c, i) => (
            <li key={c.id} className="bg-ink-50 flex items-center gap-1 rounded-lg px-2">
              <span className="min-w-0 flex-1 truncate text-sm">
                <MathText>{c.title}</MathText>{' '}
                <span className="text-ink-400 text-xs">{c.short_code}</span>
                {(otherPacks.get(c.id) ?? 0) > 0 && (
                  <span className="text-ink-500 ml-1 text-xs">
                    · aussi dans {otherPacks.get(c.id)} autre
                    {(otherPacks.get(c.id) ?? 0) > 1 ? 's' : ''} pack
                    {(otherPacks.get(c.id) ?? 0) > 1 ? 's' : ''}
                  </span>
                )}
              </span>
              <button
                type="button"
                aria-label={`Monter ${stripMath(c.title)}`}
                onClick={() => moveCard(i, -1)}
                className="flex h-11 w-9 items-center justify-center"
              >
                <ArrowUp className="h-4 w-4" />
              </button>
              <button
                type="button"
                aria-label={`Descendre ${stripMath(c.title)}`}
                onClick={() => moveCard(i, 1)}
                className="flex h-11 w-9 items-center justify-center"
              >
                <ArrowDown className="h-4 w-4" />
              </button>
              <button
                type="button"
                aria-label={`Retirer ${stripMath(c.title)}`}
                onClick={() => setCards((prev) => prev.filter((x) => x.id !== c.id))}
                className="text-ink-400 hover:text-accent-600 flex h-11 w-9 items-center justify-center"
              >
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ol>
        <input
          className={inputClass}
          placeholder="Ajouter une carte publiée du niveau (titre, domaine, code)…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Rechercher une carte à ajouter"
        />
        {query && (
          <ul className="border-ink-100 divide-ink-100 divide-y rounded-lg border">
            {candidates.length === 0 && (
              <li className="text-ink-500 px-3 py-2 text-sm">Aucune carte.</li>
            )}
            {candidates.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => {
                    setCards((prev) => [
                      ...prev,
                      { id: c.id, short_code: c.short_code, title: c.title },
                    ]);
                    setQuery('');
                  }}
                  className="hover:bg-ink-50 flex min-h-[2.75rem] w-full items-center gap-2 px-3 text-left text-sm"
                >
                  <Plus className="text-primary-500 h-4 w-4" />
                  <MathText>{c.title}</MathText>
                  <span className="text-ink-400 ml-auto text-xs">{c.domain_label}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="space-y-2">
        <p className="text-ink-700 text-sm font-semibold">Cartes à créer ({missing.length})</p>
        {missing.map((m, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2">
            <input
              className={`${inputClass} min-w-[12rem] flex-1`}
              value={m.title}
              aria-label="Titre de la carte à créer"
              onChange={(e) =>
                setMissing((prev) =>
                  prev.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)),
                )
              }
            />
            <input
              className={`${inputClass} w-40`}
              value={m.topic ?? ''}
              placeholder="Thème"
              aria-label="Thème de la carte à créer"
              onChange={(e) =>
                setMissing((prev) =>
                  prev.map((x, j) => (j === i ? { ...x, topic: e.target.value } : x)),
                )
              }
            />
            <Link
              to="/tutor/cards/new"
              search={{ title: m.title, level, subject, topic: m.topic || undefined }}
              className="text-primary-600 inline-flex min-h-[2.75rem] items-center gap-1 text-sm font-semibold"
            >
              <Sparkles className="h-4 w-4" /> Générer la carte
            </Link>
            <button
              type="button"
              aria-label="Retirer la carte à créer"
              onClick={() => setMissing((prev) => prev.filter((_, j) => j !== i))}
              className="text-ink-400 flex h-11 w-9 items-center justify-center"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => setMissing((prev) => [...prev, { title: '', topic: '' }])}
          className="text-primary-600 inline-flex min-h-[2.75rem] items-center gap-1 text-sm font-semibold"
        >
          <Plus className="h-4 w-4" /> Ajouter une carte à créer
        </button>
      </div>

      {error && (
        <p role="alert" className="text-accent-600 text-sm">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <Button type="button" onClick={() => void onSave()} disabled={save.isPending}>
          Enregistrer
        </Button>
        <Button type="button" variant="secondary" onClick={onDone}>
          Annuler
        </Button>
      </div>
    </div>
  );
}
